/**
 * BUILDER DIAGNOSTIC — behind ?ppdiag=1, invisible to everyone else.
 *
 * Built 2026-08-13 for one question: during the opening there is a shadow that
 * stays put while the card moves under it (Daniel, "a specific shadow that's
 * always fixed"). It predates the ground-shadow work — .pp-ground and
 * .pp-zoom's filter are both provably off for the whole opening — and it does
 * not reproduce in headless Chrome, in headful Chrome with a real GPU, on
 * either design, at his viewport. So it has to be bisected on the machine that
 * actually shows it.
 *
 * ⚠️ SELF-CONTAINED ON PURPOSE. It reaches into the Alpine component from the
 * outside and adds nothing to configurator.js, which is being edited in
 * parallel. Nothing here runs, and nothing is even parsed into the page's
 * behaviour, unless the query string asks for it.
 *
 * The two controls that matter:
 *   SCRUB   — park the animation on the frame where the artefact is worst.
 *             Watching it at 30fps and guessing is how the last three of these
 *             went wrong.
 *   OUTLINE — draw the border of every FIXED rectangle in the scene. A band
 *             that is screen-aligned and ignores the panels' tilt is the edge
 *             of one of these; if it lines up, the search is over.
 */
(function () {
	'use strict';

	if (!/(^|[?&])ppdiag=1(&|$)/.test(window.location.search)) return;

	var C = null;                 // the Alpine component
	var vid, cvs, cx;
	var scrubbing = false;

	/* ---- the layers that can paint during the opening ---------------------
	   `css` is applied when the box is UNCHECKED, i.e. when the layer is off.
	   Everything is !important because it has to beat stage.css. */
	var LAYERS = [
		{ id: 'shade', label: 'Shading pass (canvas)', css: '.pp-shadevid{display:none!important}' },
		{ id: 'paper', label: 'Paper + photos', css: '.pp-scene{visibility:hidden!important}' },
		{ id: 'pshadow', label: "Photos' drop shadows", css: '.pp-plane__photo--shadow{box-shadow:none!important}' },
		{ id: 'pmat', label: "Photos' mats", css: '.pp-plane__photo--slim::after,.pp-plane__photo--gallery::after{box-shadow:none!important}' },
		{ id: 'ground', label: 'Ground pool (new)', css: '.pp-ground{display:none!important}' },
		{ id: 'cardsh', label: 'Card drop-shadow (new)', css: '.pp-zoom{filter:none!important}' },
		{ id: 'stillsh', label: 'Still shading (.pp-shade)', css: '.pp-shade{display:none!important}' },
		{ id: 'bg', label: 'Stage ground (sky + grain)', css: '.pp-viewport{background-image:none!important;background-color:#fff!important}' },
	];

	var OUTLINE_CSS =
		'.pp-shadevid{outline:2px solid #FF00E5!important;outline-offset:-2px}' +
		'.pp-zoom{outline:2px solid #00E5FF!important;outline-offset:-2px}' +
		'.pp-shade{outline:2px dashed #FFD400!important;outline-offset:-2px}' +
		'.pp-ground{outline:2px solid #00FF57!important;outline-offset:-2px}' +
		'.pp-viewport{outline:2px solid #FF5A00!important;outline-offset:-2px}';

	var sheet = document.createElement('style');
	document.head.appendChild(sheet);

	function applySheet() {
		var css = '';
		LAYERS.forEach(function (L) {
			var el = document.getElementById('ppd-' + L.id);
			if (el && !el.checked) css += L.css;
		});
		var o = document.getElementById('ppd-outline');
		if (o && o.checked) css += OUTLINE_CSS;
		sheet.textContent = css;
	}

	/* ---- scrubbing --------------------------------------------------------
	   The geometry frame and the shading frame are the SAME instant, inverted:
	   the video runs closed->open while applyFrame counts open<-closed, so
	   g = 100 - round(t * 30). Driving both from one number here is the only
	   way to be sure a mismatch on screen is the artefact and not the tool. */
	function edgeFade() {
		// Identical to the fade configurator.js applies every frame. Without it
		// the shading gains a hard one-unit border and the tool invents an
		// artefact of its own.
		if (!cx) return;
		var W = cvs.width, H = cvs.height, B = Math.round(W * 72 / 2000);
		var mk = function (x0, y0, x1, y1) {
			var g = cx.createLinearGradient(x0, y0, x1, y1);
			g.addColorStop(0, 'rgba(0,0,0,1)'); g.addColorStop(1, 'rgba(0,0,0,0)');
			return g;
		};
		cx.globalCompositeOperation = 'destination-out';
		[[mk(0, 0, B, 0), 0, 0, B, H], [mk(W, 0, W - B, 0), W - B, 0, B, H],
		 [mk(0, 0, 0, B), 0, 0, W, B], [mk(0, H, 0, H - B), 0, H - B, W, B]]
			.forEach(function (e) { cx.fillStyle = e[0]; cx.fillRect(e[1], e[2], e[3], e[4]); });
		cx.globalCompositeOperation = 'source-over';
	}

	/* ⚠️ SEEK, THEN LET THE PRODUCTION LOOP SET THE POSE. Do not set cardFrame
	   here.

	   configurator.js drives the pose from the video's own presented time via
	   requestVideoFrameCallback, and rVFC still fires on a seek even while
	   paused. So an earlier version of this that ALSO called applyFrame(g)
	   fought that loop and lost: asking for frame 5 landed the video on 3.133s,
	   the loop read that as frame 6, and the tool showed a pose one frame off
	   its shading. That one-frame mismatch is the very class of artefact this
	   exists to find, and a tool that invents it is worse than no tool.

	   A seek does not land where you ask — it snaps to the nearest decodable
	   frame — so the video is the authority and the slider is a request. The
	   readout prints the frame that actually landed. */
	function drawAt(g) {
		if (!C || !vid) { report(); return; }
		var idx = Math.min(100, Math.max(0, 100 - g));
		var before = C.cardFrame;

		var settle = function () {
			// Fallback for a browser that did not run the loop off the seek:
			// draw it ourselves, exactly as configurator.js would.
			setTimeout(function () {
				if (C.cardFrame === before && cx && vid.videoWidth) {
					var landed = Math.min(100, Math.max(0, Math.round(vid.currentTime * 30)));
					C.cardFrame = 100 - landed;
					C.applyFrame(100 - landed);
					cx.globalCompositeOperation = 'source-over';
					cx.clearRect(0, 0, cvs.width, cvs.height);
					cx.drawImage(vid, 0, 0, cvs.width, cvs.height);
					edgeFade();
				}
				report();
			}, 220);
		};

		if (Math.abs(vid.currentTime - idx / 30) < 0.0005) { settle(); return; }
		vid.addEventListener('seeked', settle, { once: true });
		try { vid.currentTime = idx / 30; } catch (e) { settle(); }
	}

	function beginScrub() {
		if (!C) return;
		// ⚠️ THE STALL GUARD MUST GO FIRST. configurator.js polls currentTime
		// every 250ms and calls finishOpening(false) if it has not moved for a
		// second — so pausing to look at a frame would end the opening and
		// take the canvas away with it, which is precisely what we are trying
		// to inspect.
		if (C._stallGuard) { clearInterval(C._stallGuard); C._stallGuard = null; }
		if (vid) { try { vid.pause(); } catch (e) {} }
		scrubbing = true;
		document.getElementById('ppd-scrubwrap').style.display = '';
		drawAt(+document.getElementById('ppd-frame').value);
	}

	function replay() {
		if (!C) return;
		try { sessionStorage.removeItem('pp_opened_v2'); } catch (e) {}
		scrubbing = false;
		document.getElementById('ppd-scrubwrap').style.display = 'none';
		if (C.opening) C.finishOpening(false);
		setTimeout(function () { C.startOpening(); }, 60);
	}

	function report() {
		var vp = document.querySelector('.pp-viewport');
		var box = function (sel) {
			var e = document.querySelector(sel);
			if (!e) return 'absent';
			var r = e.getBoundingClientRect();
			if (!r.width) return 'not laid out';
			return Math.round(r.left) + ',' + Math.round(r.top) + ' ' +
			       Math.round(r.width) + '×' + Math.round(r.height);
		};
		var z = document.querySelector('[data-pp-camera]');
		armOpening(C);
		document.getElementById('ppd-out').innerHTML =
			(VERDICT
				? '<div style="margin:-2px 0 7px;padding:6px 7px;border-radius:5px;' +
				  'background:' + (VERDICT.indexOf('ANIMATED') === 0 ? '#123D25' : '#4A1020') + ';' +
				  'color:#fff;word-break:break-word">' + VERDICT + '</div>'
				: '<div style="margin:-2px 0 7px;color:#9C93B8">opening: waiting…</div>') +
			'opening: <b>' + (vp && vp.className.indexOf('pp-viewport--opening') > -1 ? 'YES' : 'no') + '</b>' +
			'  frame: <b>' + (C ? C.cardFrame : '?') + '</b>' +
			'  video t: <b>' + (vid ? vid.currentTime.toFixed(3) : '?') + '</b><br>' +
			'<span style="color:#FF00E5">canvas</span> ' + box('.pp-shadevid') +
			'  <span style="color:#00E5FF">zoom</span> ' + box('.pp-zoom') + '<br>' +
			'<span style="color:#00FF57">pool</span> ' + box('.pp-ground') +
			'  filter: ' + (z ? (getComputedStyle(z).filter === 'none' ? 'none' : 'ON') : '?');
	}

	/* ---- WHY DID THE CARD SNAP OPEN INSTEAD OF UNFOLDING? -------------------
	   Yonatan, 2026-09-11: the card jumps through three poses instead of
	   unfolding. Three theories died to guesswork (Low Power Mode, first visit
	   only, bandwidth), so this measures it on the device instead.

	   finishOpening(played) ends the opening, and `played` is TRUE only when
	   the clip ran to its end. Clip arrived too late, play() refused, a stall,
	   prefers-reduced-motion — every one of those passes FALSE and jumps
	   straight to the finished card. That flag is the answer; the rest of the
	   line says which route it took.

	   ⛔ IT ARMS ITSELF. The opening runs once, on load, so anything pasted by
	   hand is already too late — and the person who needs this is on a phone,
	   where a console means a Mac and a cable. */
	var VERDICT = null;

	function armOpening(C) {
		if (!C || C.__ppdOpeningArmed || typeof C.finishOpening !== 'function') return;
		C.__ppdOpeningArmed = true;
		var t0 = performance.now();
		var vid = document.querySelector('[data-pp-shadevid]');
		var reduced = !!(window.matchMedia &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches);

		/* ⛔ THE CLONE PROBE IS GONE. It asked a FRESH <video> whether it could
		   autoplay and reported NotAllowedError as "autoplay=REFUSED" — which
		   it duly did on Yonatan's phone, in a recording where the card
		   ANIMATED perfectly. The real element had already been granted
		   playback by _warmClip() near the navigation gesture; a clone created
		   later has no such standing, so the two answer different questions and
		   only one of them is the one being asked.

		   That is twice this probe pointed at an autoplay problem that was not
		   there — first AbortError, then NotAllowedError — and twice it would
		   have sent the search back to Low Power Mode, which Yonatan ruled out
		   by testing on 2026-09-11.

		   What replaces it is the only thing that actually matters: did the
		   REAL clip's playhead move? If the opening is driven, it moved. */
		var tStart = vid ? vid.currentTime : -1;

		var orig = C.finishOpening.bind(C);
		C.finishOpening = function (played) {
			var seek = '-';
			try {
				seek = (vid && vid.seekable && vid.seekable.length)
					? vid.seekable.start(0).toFixed(1) + '-' + vid.seekable.end(0).toFixed(1) : 'none';
			} catch (e) { seek = 'threw'; }
			var src = String((vid && (vid.currentSrc || vid.src)) || '');
			var which = /blob:/.test(src) ? 'blob'
				: (/-sm\./.test(src) ? 'small' : (/card-open/.test(src) ? 'BIG' : '?'));
			VERDICT = (played === true ? 'ANIMATED' : 'SNAPPED') +
				'  ms=' + Math.round(performance.now() - t0) +
				'  ready=' + (vid ? vid.readyState : '-') + '/4' +
				'  net=' + (vid ? vid.networkState : '-') +
				'  clip=' + which +
				'  seek=' + seek +
				'  err=' + (vid && vid.error ? vid.error.code : '-') +
				'  reduceMotion=' + (reduced ? 'ON' : 'off') +
				'  playhead=' + (vid
					? (Math.abs(vid.currentTime - tStart) > 0.05
						? 'moved(' + tStart.toFixed(2) + '->' + vid.currentTime.toFixed(2) + ')'
						: 'STUCK@' + vid.currentTime.toFixed(2))
					: 'no-video');
			if (window.console) console.log('PP DIAG  ' + VERDICT);
			return orig(played);
		};
	}

	/* ---- panel ------------------------------------------------------------ */
	function build() {
		var p = document.createElement('div');
		p.id = 'ppd';
		p.innerHTML =
			'<div id="ppd-bar"><b>ppdiag</b><button type="button" id="ppd-min">–</button></div>' +
			'<div id="ppd-body">' +
			'<div class="ppd-row"><button type="button" id="ppd-replay">Replay opening</button>' +
			'<button type="button" id="ppd-scrub">Freeze &amp; scrub</button></div>' +
			'<div id="ppd-scrubwrap" style="display:none">' +
			'<input type="range" id="ppd-frame" min="0" max="100" value="60">' +
			'<div class="ppd-hint">closed ← → open</div></div>' +
			'<label class="ppd-chk"><input type="checkbox" id="ppd-outline"> ' +
			'<span>Outline the fixed rectangles</span></label>' +
			'<hr>' +
			LAYERS.map(function (L) {
				return '<label class="ppd-chk"><input type="checkbox" id="ppd-' + L.id +
				       '" checked> <span>' + L.label + '</span></label>';
			}).join('') +
			'<div id="ppd-out"></div></div>';
		document.body.appendChild(p);

		var css = document.createElement('style');
		css.textContent =
			'#ppd{position:fixed;left:8px;bottom:8px;z-index:2147483647;width:250px;' +
			'font:12px/1.35 ui-monospace,Menlo,monospace;color:#EDE9F7;background:#1B172AF2;' +
			'border:1px solid #3E3757;border-radius:10px;overflow:hidden;' +
			'-webkit-user-select:none;user-select:none}' +
			'#ppd-bar{display:flex;align-items:center;justify-content:space-between;' +
			'padding:6px 9px;background:#2B2640;border-bottom:1px solid #3E3757}' +
			'#ppd-bar button{background:none;border:0;color:#EDE9F7;font:inherit;cursor:pointer;padding:0 4px}' +
			'#ppd-body{padding:8px 9px;max-height:62vh;overflow:auto}' +
			'#ppd.min #ppd-body{display:none}' +
			'.ppd-row{display:flex;gap:6px;margin-bottom:7px}' +
			'.ppd-row button{flex:1;background:#F5C842;color:#241F0A;border:0;border-radius:6px;' +
			'padding:6px 4px;font:600 11px/1 ui-monospace,Menlo,monospace;cursor:pointer}' +
			'#ppd-frame{width:100%;margin:2px 0 0;accent-color:#F5C842}' +
			'.ppd-hint{color:#9A93B4;font-size:10px;text-align:center;margin-bottom:6px}' +
			'.ppd-chk{display:flex;align-items:center;gap:6px;padding:2px 0;cursor:pointer}' +
			'.ppd-chk input{margin:0;accent-color:#F5C842}' +
			'#ppd hr{border:0;border-top:1px solid #3E3757;margin:7px 0}' +
			'#ppd-out{margin-top:8px;padding-top:7px;border-top:1px solid #3E3757;' +
			'color:#9A93B4;font-size:10.5px;line-height:1.5}';
		document.head.appendChild(css);

		document.getElementById('ppd-min').addEventListener('click', function () {
			p.classList.toggle('min');
		});
		document.getElementById('ppd-replay').addEventListener('click', replay);
		document.getElementById('ppd-scrub').addEventListener('click', beginScrub);
		document.getElementById('ppd-frame').addEventListener('input', function () {
			if (!scrubbing) beginScrub(); else drawAt(+this.value);
		});
		document.getElementById('ppd-outline').addEventListener('change', applySheet);
		LAYERS.forEach(function (L) {
			document.getElementById('ppd-' + L.id).addEventListener('change', applySheet);
		});
	}

	function boot() {
		var host = document.querySelector('[x-data]');
		if (!host || !window.Alpine || !window.Alpine.$data) return false;
		var d;
		try { d = window.Alpine.$data(host); } catch (e) { return false; }
		if (!d || typeof d.applyFrame !== 'function') return false;
		C = d;
		vid = document.querySelector('[data-pp-shadevid]');
		cvs = document.querySelector('[data-pp-shadecanvas]');
		cx = cvs ? cvs.getContext('2d', { alpha: true }) : null;
		build();
		setInterval(function () { if (!scrubbing) report(); }, 400);
		return true;
	}

	var tries = 0;
	var t = setInterval(function () {
		if (boot() || ++tries > 120) clearInterval(t);
	}, 250);
})();
